import React from "react";
import { FaCircle, FaEdit, FaPlus } from "react-icons/fa";
import { FaFileLines } from "react-icons/fa6";
import SalaryEvolutionChart from "../../../components/Tools/SalaryEvolutionChart";

const InfoRow = ({ label, value, icon }) => (
  <div className="flex justify-between py-2 border-b border-gray-100">
    <span className="text-gray-500 flex items-center gap-2">
      {icon}
      {label}
    </span>
    <span className="font-medium">{value}</span>
  </div>
);

const DocumentCard = ({ name }) => (
  <div className="flex items-center border px-2 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all">
    <div className="p-2 flex gap-2 items-center text-blue-600">
      <FaFileLines />
      <span className="text-sm font-medium">{name}</span>
    </div>
  </div>
);

const formatMaritalStatus = (status) => {
  switch (status) {
    case "married": return "Marié";
    case "single": return "Célibataire";
    case "divorced": return "Divorcé";
    case "widower": return "Veuve";
    default: return "Non spécifié";
  }
};

const renderLeaveBalance = (contract) => {
  const acquis = Number(contract?.leave_days_acquired) || 0;
  const pris = Number(contract?.leave_days_taken) || 0;
  const restant = Math.max(0, acquis - pris);
  const percent = acquis > 0 ? Math.min(100, Math.round((pris / acquis) * 100)) : 0;

  return (
    <>
      <div className="mb-4">
        <div className="w-full bg-gray-200 flex rounded-full h-2 mb-2 overflow-hidden">
          <div className="bg-orange-500 h-2" style={{ width: `${percent}%` }}></div>
          <div className="bg-green-500 h-2" style={{ width: `${100 - percent}%` }}></div>
        </div>
      </div>
      <div className="space-y-3">
        <InfoRow icon={<FaCircle className="text-blue-500 text-xs" />} label="Acquis" value={`${acquis} jours`} />
        <InfoRow icon={<FaCircle className="text-orange-500 text-xs" />} label="Pris" value={`${pris} jours`} />
        <InfoRow icon={<FaCircle className="text-green-500 text-xs" />} label="Restant" value={`${restant} jours`} />
      </div>
    </>
  );
};

const Informations = ({ employee, payslipData, currentContract, setIsDrawerOpen }) => {
  const { name, maritalStatus, numberOfChildren, spouse_income, nationality, address, email } = employee || {};

  const salaryHistory = [
    { year: "-", salary: "5 400 000" },
    { year: 2024, salary: "5 000 000" },
    { year: 2023, salary: "3 200 000" },
    { year: 2022, salary: "2 500 000" },
  ];

  return (
    <div className="p-4 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

        {/* Documents d'embauche */}
        {/* <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Documents D'embauche</h3>
              <button className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 text-sm font-medium flex items-center gap-1">
                <FaPlus className="text-xs" /> Ajouter
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DocumentCard name="cv_francais.pdf" />
              <DocumentCard name="contrat_de_travial.pdf" />
            </div>
          </div>
        </div> */}

        {/* Informations personnelles */}
        <div className="col-span-1 md:col-span-2 md:row-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <span className="w-2 h-6 bg-blue-500 rounded-sm mr-2" />
                Informations personnelles
              </h3>
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium flex items-center gap-1"
              >
                <FaEdit className="text-xs" /> Modifier
              </button>
            </div>
            <div className="space-y-3">
              <InfoRow label="Nom complet" value={name || "-"} />
              <InfoRow label="Situation Matrimoniale" value={formatMaritalStatus(maritalStatus)} />
              <InfoRow label="Nombre d'enfants" value={numberOfChildren ?? "-"} />
              <InfoRow label="Conjoint avec revenu" value={spouse_income ? "OUI" : "Non"} />
              <InfoRow label="Nationalité" value={nationality || "-"} />
              <InfoRow label="Adresse" value={address || "-"} />
              <InfoRow label="Email" value={email || "-"} />
            </div>
          </div>
        </div>

        {/* Salaire du mois */}
        <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <h3 className="text-lg font-bold mb-4">Salaire du mois</h3>
            <div className="space-y-3">
              <InfoRow
                label="Salaire Brut"
                value={`${(payslipData?.gross_salary || 0).toLocaleString("fr-FR")} FCFA`}
              />
              <InfoRow
                label="Salaire Net"
                value={`${(payslipData?.net_salary || 0).toLocaleString("fr-FR")} FCFA`}
              />
            </div>
          </div>
        </div>

        {/* Solde congés */}
        <div className="col-span-1 md:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-4 h-full">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <span className="w-2 h-6 bg-orange-500 rounded-sm mr-2" />
              Solde Congés
            </h3>
            {renderLeaveBalance(currentContract)}
          </div>
        </div>
      </div>

      {/* Graphique salaire */}
      <div className="mt-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-lg font-bold mb-6">Graphe des salaires</h3>
          <SalaryEvolutionChart employeeId={employee.id} />
        </div>
      </div>
    </div>
  );
};

export default Informations;
